import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API, auth, errMsg, PageHead, PrimaryBtn, Modal, Field, inputCls } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';

const BLANK = { name: '', sport: '', city: '', min_age: '', max_age: '', price: '', currency: 'SAR', billing_period: 'شهرياً', schedule_text: '', available_slots: '', description: '', is_active: true };

export default function ProgramsPage() {
  const [programs, setPrograms] = useState(null);
  const [toast, setToast] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/programs`, auth());
      setPrograms(res.data || []);
    } catch { setPrograms([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(BLANK); setModalOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, sport: p.sport, city: p.city || '', min_age: p.min_age ?? '', max_age: p.max_age ?? '',
      price: p.price ?? '', currency: p.currency || 'SAR', billing_period: p.billing_period || 'شهرياً',
      schedule_text: p.schedule_text || '', available_slots: p.available_slots ?? '', description: p.description || '', is_active: p.is_active,
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.sport.trim()) { notify('اسم البرنامج والرياضة مطلوبان'); return; }
    setSaving(true);
    const payload = {
      ...form,
      min_age: form.min_age === '' ? null : Number(form.min_age),
      max_age: form.max_age === '' ? null : Number(form.max_age),
      price: form.price === '' ? 0 : Number(form.price),
      available_slots: form.available_slots === '' ? 0 : Number(form.available_slots),
    };
    try {
      if (editing) await axios.put(`${API}/programs/${editing.id}`, payload, auth());
      else await axios.post(`${API}/programs`, payload, auth());
      notify(editing ? 'تم تحديث البرنامج' : 'تم إنشاء البرنامج بنجاح');
      setModalOpen(false);
      load();
    } catch (e) { notify(errMsg(e, 'فشل حفظ البرنامج')); } finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/programs/${p.id}`, { is_active: !p.is_active }, auth());
      load();
    } catch (e) { notify(errMsg(e, 'فشل التحديث')); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/programs/${confirmDelete.id}`, auth());
      notify('تم حذف البرنامج');
      setConfirmDelete(null);
      load();
    } catch (e) { notify(errMsg(e, 'فشل الحذف')); }
  };

  return (
    <div className="space-y-4" data-testid="programs-page">
      <PageHead
        title="برامج الأكاديمية"
        desc="أضف البرامج التي تظهر لأولياء الأمور في دليل الاستكشاف مع الأعمار والأسعار والمقاعد المتاحة"
        action={<PrimaryBtn onClick={openCreate} data-testid="program-add-btn"><Icon name="plus" className="h-4 w-4" /> برنامج جديد</PrimaryBtn>}
      />

      {toast && <div className="rounded-lg bg-teal-50 px-4 py-2 text-sm text-teal-700" data-testid="programs-toast">{toast}</div>}

      {programs === null ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>
      ) : programs.length === 0 ? (
        <EmptyState icon="layers" text="لا توجد برامج بعد — أضف أول برنامج ليظهر لأولياء الأمور" testId="programs-empty" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="programs-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-right text-xs text-slate-500">
              <tr>
                <th className="px-4 py-3">البرنامج</th>
                <th className="px-4 py-3">الرياضة</th>
                <th className="px-4 py-3">المدينة</th>
                <th className="px-4 py-3">الأعمار</th>
                <th className="px-4 py-3">السعر</th>
                <th className="px-4 py-3">المقاعد</th>
                <th className="px-4 py-3">الحالة</th>
                <th className="px-4 py-3">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`program-row-${p.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{p.name}</td>
                  <td className="px-4 py-3 text-slate-600">{p.sport}</td>
                  <td className="px-4 py-3 text-slate-600">{p.city || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{p.min_age || 0}–{p.max_age || '+'}</td>
                  <td className="px-4 py-3 text-slate-600">{p.price} ر.س / {p.billing_period}</td>
                  <td className="px-4 py-3 text-slate-600">{Math.max(0, (p.available_slots || 0) - (p.enrolled_count || 0))} / {p.available_slots || 0}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(p)} data-testid={`program-toggle-${p.id}`} className={`rounded-full px-2.5 py-1 text-xs font-bold ${p.is_active ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                      {p.is_active ? 'مفعّل' : 'معطّل'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(p)} data-testid={`program-edit-${p.id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">تعديل</button>
                      <button onClick={() => setConfirmDelete(p)} data-testid={`program-delete-${p.id}`} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">حذف</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modalOpen} title={editing ? 'تعديل البرنامج' : 'برنامج جديد'} onClose={() => setModalOpen(false)} wide>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="اسم البرنامج" required><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="program-form-name" /></Field>
          <Field label="الرياضة" required><input className={inputCls} value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} data-testid="program-form-sport" /></Field>
          <Field label="المدينة"><input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="program-form-city" /></Field>
          <Field label="مدة الاشتراك"><input className={inputCls} value={form.billing_period} onChange={(e) => setForm({ ...form, billing_period: e.target.value })} placeholder="شهرياً" data-testid="program-form-billing-period" /></Field>
          <Field label="أقل عمر"><input type="number" min="0" className={inputCls} value={form.min_age} onChange={(e) => setForm({ ...form, min_age: e.target.value })} data-testid="program-form-min-age" /></Field>
          <Field label="أعلى عمر"><input type="number" min="0" className={inputCls} value={form.max_age} onChange={(e) => setForm({ ...form, max_age: e.target.value })} data-testid="program-form-max-age" /></Field>
          <Field label="السعر (ر.س)"><input type="number" min="0" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="program-form-price" /></Field>
          <Field label="المقاعد المتاحة"><input type="number" min="0" className={inputCls} value={form.available_slots} onChange={(e) => setForm({ ...form, available_slots: e.target.value })} data-testid="program-form-slots" /></Field>
          <Field label="مواعيد التدريب"><input className={inputCls} value={form.schedule_text} onChange={(e) => setForm({ ...form, schedule_text: e.target.value })} placeholder="مثال: السبت والثلاثاء 5-7 مساءً" data-testid="program-form-schedule" /></Field>
          <label className="flex items-center gap-2 self-end pb-2 text-sm font-semibold text-slate-600">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="program-form-active" /> مفعّل ويظهر لأولياء الأمور
          </label>
          <div className="sm:col-span-2">
            <Field label="وصف البرنامج"><textarea className={inputCls} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="program-form-description" /></Field>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => setModalOpen(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">إلغاء</button>
          <PrimaryBtn onClick={save} disabled={saving} data-testid="program-form-submit">{saving ? 'جاري الحفظ...' : 'حفظ البرنامج'}</PrimaryBtn>
        </div>
      </Modal>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="program-delete-confirm-modal">
          <div className="w-full max-w-sm rounded-xl bg-white p-6">
            <h4 className="font-bold text-slate-900">تأكيد الحذف</h4>
            <p className="mt-2 text-sm text-slate-500">سيتم حذف برنامج «{confirmDelete.name}» نهائياً. هل أنت متأكد؟</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">إلغاء</button>
              <button onClick={remove} data-testid="program-delete-confirm-btn" className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white">تأكيد الحذف</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
